import {
  DataGrid,
  FilterToolbar,
  formatCurrency,
  formatDate,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  AnnuityCarrierTransactionRes,
  GetAnnuityCarrierTransactionsParams,
  RkTransactionType,
  useGetAnnuityCarrierTransactions
} from '@ijoin/hooks';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { FC, useState } from 'react';

import { CarrierTransactionActionsCell } from './CarrierTransactionActionsCell';
import { CarrierTransactionDetailsDialog } from './CarrierTransactionDetailsDialog';

const TRANSACTION_TYPE_OPTIONS: {
  label: string;
  value: RkTransactionType;
}[] = [
  { label: '01 / Contribution', value: '01' },
  { label: '02 / Exchange Buy', value: '02' },
  { label: '03 / Exchange Sell', value: '03' },
  { label: '05 / Fee', value: '05' },
  { label: '07 / Disbursements', value: '07' },
  { label: '09 / Loan Repayment', value: '09' },
  { label: '10 / Plan Transfer In', value: '10' },
  { label: '11 / Plan Transfer Out', value: '11' },
  { label: '12 / Rollover In', value: '12' },
  { label: '17 / Annuitization', value: '17' }
];

const FILTER_DEFS: FilterDef[] = [
  { label: 'Participant', name: 'participant', type: 'text' },
  { label: 'Transaction ID', name: 'id', type: 'text' },
  { label: 'Plan', name: 'planDescription', type: 'text' },
  { label: 'CUSIP', name: 'cusip', type: 'text' },
  {
    label: 'Type',
    name: 'transactionType',
    options: TRANSACTION_TYPE_OPTIONS,
    type: 'select'
  },
  { label: 'Account Number', name: 'accountNumber', type: 'text' },
  {
    format: 'currency',
    label: 'Amount',
    min: -Number.MAX_SAFE_INTEGER,
    name: 'totalDollarAmt',
    type: 'number'
  },
  {
    label: 'Reversal',
    name: 'reversalIndicator',
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' }
    ],
    type: 'select'
  },
  {
    endName: 'endPostDate',
    label: 'Post Date',
    startName: 'startPostDate',
    type: 'dateRange'
  }
];

export const CarrierTransactionsTab: FC<{
  annuityIntegrationId: string;
}> = props => {
  const [query, setQuery] = useState<GridQuery>();
  const [selected, setSelected] = useState<AnnuityCarrierTransactionRes | null>(
    null
  );

  const transactions = useGetAnnuityCarrierTransactions(
    props.annuityIntegrationId,
    query as GetAnnuityCarrierTransactionsParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const onCloseDetails = () => setSelected(null);

  const columns: GridColDef[] = [
    {
      align: 'center',
      disableColumnMenu: true,
      display: 'flex',
      field: 'details',
      filterable: false,
      headerName: '',
      renderCell: params => (
        <IconButton
          aria-label='Details'
          data-testid={`details-${params.row.id}`}
          onClick={() => setSelected(params.row)}>
          <InfoOutlinedIcon />
        </IconButton>
      ),
      sortable: false,
      width: 67
    },
    {
      field: 'postDate',
      flex: 1,
      headerName: 'Date',
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MM/DD/YYYY', useUtc: true }) : ''
    },
    { field: 'id', flex: 0.8, headerName: 'ID', type: 'number' },
    {
      field: 'participantName',
      flex: 1,
      headerName: 'Participant',
      sortable: false
    },
    {
      field: 'transactionType',
      flex: 1,
      headerName: 'Type',
      sortable: false
    },
    { field: 'cusip', flex: 1, headerName: 'CUSIP', sortable: false },
    {
      field: 'planDescription',
      flex: 1,
      headerName: 'Plan',
      renderCell: params => (
        <Link
          data-testid={`planDescription-${params.row.id}`}
          params={{
            advisoryOrganizationId: params.row.advisoryOrganizationId,
            planId: params.row.planId
          }}
          to='/organizations/$advisoryOrganizationId/plans/$planId'>
          {params.value}
        </Link>
      ),
      sortable: false
    },
    {
      field: 'accountNumber',
      flex: 1,
      headerName: 'Account Number',
      sortable: false
    },
    {
      field: 'reversalIndicator',
      flex: 0.6,
      headerName: 'Reversal',
      renderCell: params => (params.value ? 'Yes' : 'No')
    },
    {
      field: 'reversesId',
      flex: 0.8,
      headerName: 'Reverses ID',
      sortable: false
    },
    {
      field: 'totalDollarAmt',
      flex: 1,
      headerName: 'Amount',
      type: 'number',
      valueFormatter: value => (value == null ? '' : formatCurrency(value))
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <CarrierTransactionActionsCell
          annuityIntegrationId={props.annuityIntegrationId}
          transaction={params.row}
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={FILTER_DEFS}
        getRowId={row => row.id}
        initialSort={[{ field: 'postDate', sort: 'desc' }]}
        loading={!query || transactions.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        rowCount={transactions.data?.pagination?.total ?? 0}
        rows={transactions.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'carrierTransactions'
          },
          toolbar: { onRefresh: transactions.refetch }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />
      {selected && (
        <CarrierTransactionDetailsDialog
          onClose={onCloseDetails}
          transaction={selected}
        />
      )}
    </>
  );
};

CarrierTransactionsTab.displayName = 'CarrierTransactionsTab';
